import { AlertCircle } from "lucide-react";
import React from "react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";

export interface ErrorStateProps {
  /**
   * The main error title to display
   */
  title?: string;

  /**
   * The error message or description
   */
  message: string;

  /**
   * Optional icon to display (defaults to AlertCircle)
   */
  icon?: React.ReactNode;

  /**
   * Optional action button configuration
   */
  action?: {
    label: string;
    onClick: () => void;
    variant?:
      | "default"
      | "destructive"
      | "outline"
      | "secondary"
      | "ghost"
      | "link";
  };

  /**
   * Optional secondary action button
   */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?:
      | "default"
      | "destructive"
      | "outline"
      | "secondary"
      | "ghost"
      | "link";
  };

  /**
   * Custom className for additional styling
   */
  className?: string;
}

/**
 * ErrorState Component
 *
 * A reusable error state component using Shadcn Alert.
 * Used across the application to display errors consistently.
 *
 * @example
 * ```tsx
 * <ErrorState
 *   title="Failed to Load Data"
 *   message="Could not fetch deployment details. Please try again."
 *   action={{
 *     label: "Retry",
 *     onClick: () => refetch()
 *   }}
 *   secondaryAction={{
 *     label: "Go Back",
 *     onClick: () => navigate(-1),
 *     variant: "outline"
 *   }}
 * />
 * ```
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title = "Error",
  message,
  icon,
  action,
  secondaryAction,
  className = "",
}) => {
  return (
    <div className={`container mx-auto px-4 py-8 ${className}`}>
      <Alert variant="destructive" className="max-w-2xl mx-auto">
        {icon || <AlertCircle className="h-4 w-4" />}
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          <p className="mb-4">{message}</p>
          {(action || secondaryAction) && (
            <div className="flex gap-2 mt-4">
              {action && (
                <Button
                  variant={action.variant || "default"}
                  onClick={action.onClick}
                  size="sm"
                >
                  {action.label}
                </Button>
              )}
              {secondaryAction && (
                <Button
                  variant={secondaryAction.variant || "outline"}
                  onClick={secondaryAction.onClick}
                  size="sm"
                >
                  {secondaryAction.label}
                </Button>
              )}
            </div>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default ErrorState;
